import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  DocumentTextIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  TrashIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  PaperAirplaneIcon,
  BanknotesIcon,
  ArrowPathIcon,
  UserIcon,
  AcademicCapIcon,
  CalendarIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import toast from 'react-hot-toast'
import {
  getInvoices,
  getInvoiceById,
  createInvoice,
  recordPayment,
  sendInvoice,
  cancelInvoice,
  deleteInvoice,
  getInvoiceStats,
  type Invoice,
  type InvoiceStatus,
  type InvoiceType,
  type InvoiceStats,
  type CreateInvoiceData,
  type InvoiceFilters,
  getTypeLabel,
  formatCurrency,
} from '@/api/invoiceApi'
import { getUsers, type User } from '@/api/usersApi'
import { getRoles, type Role } from '@/api/rolesApi'

const createInvoiceSchema = z.object({
  type: z.enum(['SCHOOL_FEES', 'TRANSPORT', 'LIBRARY', 'UNIFORM', 'BOOKS', 'MEALS', 'ACTIVITIES', 'EXAM_FEES', 'OTHER']),
  customerType: z.enum(['STUDENT', 'PARENT', 'STAFF', 'EXTERNAL']),
  customerName: z.string().min(1, 'Customer name is required'),
  customerEmail: z.string().email().optional().or(z.literal('')),
  customerPhone: z.string().optional(),
  studentName: z.string().optional(),
  title: z.string().min(1, 'Invoice title is required'),
  description: z.string().optional(),
  dueDate: z.string().min(1, 'Due date is required'),
  termName: z.string().optional(),
  academicYear: z.string().optional(),
  notes: z.string().optional(),
})

const paymentSchema = z.object({
  amount: z.number().min(0.01, 'Amount must be greater than 0'),
  paymentMethod: z.enum(['CASH', 'MPESA', 'BANK_TRANSFER', 'CARD', 'CHEQUE', 'WALLET']),
  reference: z.string().optional(),
  notes: z.string().optional(),
})

type CreateInvoiceFormData = z.infer<typeof createInvoiceSchema>
type PaymentFormData = z.infer<typeof paymentSchema>

const statusColors: Record<InvoiceStatus, { bg: string; text: string; dot: string }> = {
  DRAFT: { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-400' },
  PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-400' },
  SENT: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-400' },
  PARTIALLY_PAID: { bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-400' },
  PAID: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-400' },
  OVERDUE: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-400' },
  CANCELLED: { bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-400' },
  REFUNDED: { bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-400' },
}

const InvoicesPage: React.FC = () => {
  const navigate = useNavigate()
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<InvoiceStats | null>(null)

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | ''>('')
  const [typeFilter, setTypeFilter] = useState<InvoiceType | ''>('')
  const [showOverdueOnly, setShowOverdueOnly] = useState(false)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null)

  // Student/Parent search for invoice creation
  const [roles, setRoles] = useState<Role[]>([])
  const [studentSearch, setStudentSearch] = useState('')
  const [studentSearchResults, setStudentSearchResults] = useState<User[]>([])
  const [searchingStudents, setSearchingStudents] = useState(false)
  const [selectedStudent, setSelectedStudent] = useState<User | null>(null)
  const [showStudentDropdown, setShowStudentDropdown] = useState(false)
  // Parent search
  const [parentSearch, setParentSearch] = useState('')
  const [parentSearchResults, setParentSearchResults] = useState<User[]>([])
  const [searchingParents, setSearchingParents] = useState(false)
  const [selectedParent, setSelectedParent] = useState<User | null>(null)
  const [showParentDropdown, setShowParentDropdown] = useState(false)
  const [selectedChildFromParent, setSelectedChildFromParent] = useState<{ id: string; fullName: string; studentId?: string } | null>(null)
  // Staff/Teacher search
  const [staffSearch, setStaffSearch] = useState('')
  const [staffSearchResults, setStaffSearchResults] = useState<User[]>([])
  const [searchingStaff, setSearchingStaff] = useState(false)
  const [selectedStaff, setSelectedStaff] = useState<User | null>(null)
  const [showStaffDropdown, setShowStaffDropdown] = useState(false)

  // Items for new invoice
  const [invoiceItems, setInvoiceItems] = useState<Array<{
    description: string
    quantity: number
    unitPrice: number
    discount: number
    discountType: 'FIXED' | 'PERCENTAGE'
    taxRate: number
  }>>([
    { description: '', quantity: 1, unitPrice: 0, discount: 0, discountType: 'FIXED', taxRate: 0 }
  ])

  const createForm = useForm<CreateInvoiceFormData>({
    resolver: zodResolver(createInvoiceSchema),
    defaultValues: {
      type: 'SCHOOL_FEES',
      customerType: 'STUDENT',
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      studentName: '',
      title: '',
      description: '',
      dueDate: '',
      termName: '',
      academicYear: '',
      notes: '',
    }
  })

  const paymentForm = useForm<PaymentFormData>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      amount: 0,
      paymentMethod: 'CASH',
      reference: '',
      notes: '',
    }
  })

  useEffect(() => {
    fetchInvoices()
    fetchStats()
    fetchRoles()
  }, [currentPage, statusFilter, typeFilter, showOverdueOnly])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentPage === 1) {
        fetchInvoices()
      } else {
        setCurrentPage(1)
      }
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  // Fetch roles to get student role ID
  const fetchRoles = async () => {
    try {
      const response = await getRoles()
      setRoles(response.data || [])
    } catch (error) {
      console.error('Error fetching roles:', error)
    }
  }

  // Get the student role ID
  const studentRoleId = useMemo(() => {
    const studentRole = roles.find(r => r.name.toLowerCase() === 'student')
    return studentRole?.id
  }, [roles])

  // Get the parent role ID
  const parentRoleId = useMemo(() => {
    const parentRole = roles.find(r => r.name.toLowerCase() === 'parent')
    return parentRole?.id
  }, [roles])

  // Get the teacher role ID
  const teacherRoleId = useMemo(() => {
    const teacherRole = roles.find(r => r.name.toLowerCase() === 'teacher')
    return teacherRole?.id
  }, [roles])

  // Debounced student search using useEffect
  useEffect(() => {
    if (!studentSearch.trim() || !studentRoleId) {
      setStudentSearchResults([])
      return
    }

    setSearchingStudents(true)
    const timeoutId = setTimeout(async () => {
      try {
        const response = await getUsers({
          search: studentSearch,
          roleId: studentRoleId,
          limit: 10,
          status: 'active'
        })
        setStudentSearchResults(response.users || [])
      } catch (error) {
        console.error('Error searching students:', error)
        setStudentSearchResults([])
      } finally {
        setSearchingStudents(false)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [studentSearch, studentRoleId])

  // Handle student search input change
  const handleStudentSearchChange = useCallback((value: string) => {
    setStudentSearch(value)
    setShowStudentDropdown(true)
  }, [])

  // Debounced parent search using useEffect
  useEffect(() => {
    if (!parentSearch.trim() || !parentRoleId) {
      setParentSearchResults([])
      return
    }

    setSearchingParents(true)
    const timeoutId = setTimeout(async () => {
      try {
        const response = await getUsers({
          search: parentSearch,
          roleId: parentRoleId,
          limit: 10,
          status: 'active'
        })
        setParentSearchResults(response.users || [])
      } catch (error) {
        console.error('Error searching parents:', error)
        setParentSearchResults([])
      } finally {
        setSearchingParents(false)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [parentSearch, parentRoleId])

  // Handle parent search input change
  const handleParentSearchChange = useCallback((value: string) => {
    setParentSearch(value)
    setShowParentDropdown(true)
  }, [])

  // Handle parent selection
  const handleSelectParent = useCallback((parent: User) => {
    setSelectedParent(parent)
    setParentSearch(parent.fullName || `${parent.firstName} ${parent.lastName}`)
    setShowParentDropdown(false)
    setParentSearchResults([])

    // Auto-fill form with parent info
    createForm.setValue('customerName', parent.fullName || `${parent.firstName} ${parent.lastName}`)
    createForm.setValue('customerEmail', parent.email || '')
    createForm.setValue('customerPhone', parent.phone || '')

    // If parent has exactly one child, auto-select it
    if (parent.children && parent.children.length === 1) {
      const child = parent.children[0]
      setSelectedChildFromParent(child)
      createForm.setValue('studentName', child.fullName || '')
    } else if (parent.children && parent.children.length > 1) {
      // Multiple children - user needs to select one
      setSelectedChildFromParent(null)
      createForm.setValue('studentName', '')
    } else {
      setSelectedChildFromParent(null)
      createForm.setValue('studentName', '')
    }
  }, [createForm])

  // Handle child selection from parent's children
  const handleSelectChildFromParent = useCallback((child: { id: string; fullName: string; studentId?: string }) => {
    setSelectedChildFromParent(child)
    createForm.setValue('studentName', child.fullName || '')
  }, [createForm])

  // Clear parent selection
  const clearParentSelection = useCallback(() => {
    setSelectedParent(null)
    setSelectedChildFromParent(null)
    setParentSearch('')
    setParentSearchResults([])
  }, [])

  // Debounced staff search using useEffect
  useEffect(() => {
    if (!staffSearch.trim() || !teacherRoleId) {
      setStaffSearchResults([])
      return
    }

    setSearchingStaff(true)
    const timeoutId = setTimeout(async () => {
      try {
        const response = await getUsers({
          search: staffSearch,
          roleId: teacherRoleId,
          limit: 10,
          status: 'active'
        })
        setStaffSearchResults(response.users || [])
      } catch (error) {
        console.error('Error searching staff:', error)
        setStaffSearchResults([])
      } finally {
        setSearchingStaff(false)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [staffSearch, teacherRoleId])

  // Handle staff search input change
  const handleStaffSearchChange = useCallback((value: string) => {
    setStaffSearch(value)
    setShowStaffDropdown(true)
  }, [])

  // Handle staff selection
  const handleSelectStaff = useCallback((staff: User) => {
    setSelectedStaff(staff)
    setStaffSearch(staff.fullName || `${staff.firstName} ${staff.lastName}`)
    setShowStaffDropdown(false)
    setStaffSearchResults([])

    // Auto-fill form with staff info
    createForm.setValue('customerName', staff.fullName || `${staff.firstName} ${staff.lastName}`)
    createForm.setValue('customerEmail', staff.email || '')
    createForm.setValue('customerPhone', staff.phone || '')
    createForm.setValue('studentName', '')
  }, [createForm])

  // Clear staff selection
  const clearStaffSelection = useCallback(() => {
    setSelectedStaff(null)
    setStaffSearch('')
    setStaffSearchResults([])
  }, [])

  // Handle student selection
  const handleSelectStudent = useCallback((student: User) => {
    setSelectedStudent(student)
    setStudentSearch(student.fullName || `${student.firstName} ${student.lastName}`)
    setShowStudentDropdown(false)
    setStudentSearchResults([])

    // Auto-fill form with student and parent info
    createForm.setValue('studentName', student.fullName || `${student.firstName} ${student.lastName}`)

    // If student has parent info, use it for customer details and set customerType to PARENT
    if (student.primaryParent) {
      createForm.setValue('customerType', 'PARENT') // Bill goes to parent
      createForm.setValue('customerName', student.primaryParent.fullName)
      createForm.setValue('customerEmail', student.primaryParent.email || '')
      createForm.setValue('customerPhone', student.primaryParent.phone || '')
    } else if (student.parents && student.parents.length > 0) {
      const parent = student.parents[0]
      createForm.setValue('customerType', 'PARENT') // Bill goes to parent
      createForm.setValue('customerName', parent.fullName)
      createForm.setValue('customerEmail', parent.email || '')
      createForm.setValue('customerPhone', parent.phone || '')
    } else {
      // No parent info - use student info as customer
      createForm.setValue('customerName', student.fullName || `${student.firstName} ${student.lastName}`)
      createForm.setValue('customerEmail', student.email || '')
      createForm.setValue('customerPhone', student.phone || '')
    }
  }, [createForm])

  // Clear student selection
  const clearStudentSelection = useCallback(() => {
    setSelectedStudent(null)
    setStudentSearch('')
    setStudentSearchResults([])
  }, [])

  // Clear all selections and form fields
  const clearAllSelections = useCallback(() => {
    clearStudentSelection()
    clearParentSelection()
    clearStaffSelection()
    createForm.setValue('studentName', '')
    createForm.setValue('customerName', '')
    createForm.setValue('customerEmail', '')
    createForm.setValue('customerPhone', '')
  }, [createForm, clearStudentSelection, clearParentSelection, clearStaffSelection])

  const fetchInvoices = async () => {
    setLoading(true)
    try {
      const filters: InvoiceFilters = {
        page: currentPage,
        limit: 20,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      }
      if (searchQuery) filters.search = searchQuery
      if (statusFilter) filters.status = statusFilter
      if (typeFilter) filters.type = typeFilter
      if (showOverdueOnly) filters.overdue = true

      const response = await getInvoices(filters)
      setInvoices(response.data.invoices)
      setTotalPages(response.data.pagination.pages)
      setTotalCount(response.data.pagination.total)
    } catch (error) {
      console.error('Error fetching invoices:', error)
      toast.error('Failed to fetch invoices')
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await getInvoiceStats()
      setStats(response.data)
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  const handleCreateInvoice = async (data: CreateInvoiceFormData) => {
    try {
      // Prepare items without the 'total' field (backend calculates it)
      const processedItems = invoiceItems.map(item => ({
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        discount: item.discount,
        discountType: item.discountType,
        taxRate: item.taxRate,
      }))

      if (processedItems.length === 0 || processedItems.some(item => !item.description)) {
        toast.error('Please add at least one valid item')
        return
      }

      // Determine studentId and parentId based on customer type and selection flow
      let studentId: string | undefined
      let parentId: string | undefined
      let gradeId: string | undefined
      let streamId: string | undefined

      if (data.customerType === 'STUDENT' && selectedStudent) {
        // Flow 1: Selected student first, parent auto-filled
        studentId = selectedStudent._id || selectedStudent.id
        parentId = selectedStudent.primaryParent?.id || selectedStudent.parents?.[0]?.id
        gradeId = selectedStudent.gradeId
        streamId = selectedStudent.streamId
      } else if (data.customerType === 'PARENT' && selectedParent) {
        // Flow 2: Selected parent first, then selected child
        parentId = selectedParent._id || selectedParent.id
        if (selectedChildFromParent) {
          studentId = selectedChildFromParent.id
        }
      }

      // Log for debugging
      console.log('Creating invoice with:', {
        customerType: data.customerType,
        selectedStudent,
        selectedParent,
        selectedChildFromParent,
        studentId,
        parentId,
      })

      const invoiceData: CreateInvoiceData = {
        type: data.type,
        customerType: data.customerType,
        customerName: data.customerName,
        customerEmail: data.customerEmail || undefined,
        customerPhone: data.customerPhone || undefined,
        studentName: data.studentName || undefined,
        title: data.title,
        description: data.description || undefined,
        dueDate: data.dueDate,
        termName: data.termName || undefined,
        academicYear: data.academicYear || undefined,
        notes: data.notes || undefined,
        items: processedItems,
        // Add studentId if available
        ...(studentId && { studentId }),
        // Add parentId and customerId if available (parent is the bill-to)
        ...(parentId && {
          parentId,
          customerId: parentId,
        }),
        // Add grade and stream IDs if available
        ...(gradeId && { gradeId }),
        ...(streamId && { streamId }),
      }

      await createInvoice(invoiceData)
      toast.success('Invoice created successfully')
      resetCreateForm()
      fetchInvoices()
      fetchStats()
    } catch (error: any) {
      console.error('Error creating invoice:', error)
      toast.error(error.response?.data?.message || 'Failed to create invoice')
    }
  }

  // Reset create form and close modal
  const resetCreateForm = () => {
    setShowCreateModal(false)
    createForm.reset()
    setInvoiceItems([{ description: '', quantity: 1, unitPrice: 0, discount: 0, discountType: 'FIXED' as const, taxRate: 0 }])
    clearAllSelections()
  }

  const handleRecordPayment = async (data: PaymentFormData) => {
    if (!selectedInvoice) return

    try {
      await recordPayment(selectedInvoice._id, {
        amount: data.amount,
        paymentMethod: data.paymentMethod,
        reference: data.reference || undefined,
        notes: data.notes || undefined,
      })
      toast.success('Payment recorded successfully')
      setShowPaymentModal(false)
      paymentForm.reset()
      fetchInvoices()
      fetchStats()
      // Refresh selected invoice
      const response = await getInvoiceById(selectedInvoice._id)
      setSelectedInvoice(response.data)
    } catch (error: any) {
      console.error('Error recording payment:', error)
      toast.error(error.response?.data?.message || 'Failed to record payment')
    }
  }

  const handleSendInvoice = async (invoice: Invoice) => {
    try {
      await sendInvoice(invoice._id)
      toast.success('Invoice sent successfully')
      fetchInvoices()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send invoice')
    }
  }

  const handleCancelInvoice = async (invoice: Invoice) => {
    const reason = prompt('Please provide a reason for cancellation:')
    if (!reason) return

    try {
      await cancelInvoice(invoice._id, reason)
      toast.success('Invoice cancelled successfully')
      fetchInvoices()
      fetchStats()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to cancel invoice')
    }
  }

  const handleDeleteInvoice = async (invoice: Invoice) => {
    if (!confirm('Are you sure you want to delete this draft invoice?')) return

    try {
      await deleteInvoice(invoice._id)
      toast.success('Invoice deleted successfully')
      fetchInvoices()
      fetchStats()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete invoice')
    }
  }

  const viewInvoice = (invoice: Invoice) => {
    // Navigate to invoice detail page
    navigate(`/schoolAdmin/invoices/${invoice._id}`)
  }

  const openPaymentModal = (invoice: Invoice) => {
    setSelectedInvoice(invoice)
    paymentForm.setValue('amount', invoice.balanceDue)
    setShowPaymentModal(true)
  }

  const addItem = () => {
    setInvoiceItems([...invoiceItems, { description: '', quantity: 1, unitPrice: 0, discount: 0, discountType: 'FIXED', taxRate: 0 }])
  }

  const removeItem = (index: number) => {
    setInvoiceItems(invoiceItems.filter((_, i) => i !== index))
  }

  const updateItem = (index: number, field: string, value: any) => {
    const updated = [...invoiceItems]
    updated[index] = { ...updated[index], [field]: value }
    setInvoiceItems(updated)
  }

  const calculateTotal = () => {
    return invoiceItems.reduce((sum, item) => {
      const itemTotal = item.quantity * item.unitPrice
      const discount = item.discountType === 'PERCENTAGE'
        ? itemTotal * (item.discount / 100)
        : item.discount
      const taxable = itemTotal - discount
      const tax = taxable * (item.taxRate / 100)
      return sum + taxable + tax
    }, 0)
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-500 mt-1">Manage and track invoices for fees and payments</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2">
          <PlusIcon className="w-5 h-5" />
          Create Invoice
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Invoices</p>
                <p className="text-2xl font-bold text-gray-900">{stats.summary.totalInvoices}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <DocumentTextIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Amount</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.summary.totalAmount)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CurrencyDollarIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Amount Paid</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.summary.totalPaid)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircleIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Outstanding</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(stats.summary.totalOutstanding)}</p>
                {stats.overdueCount > 0 && (
                  <p className="text-xs text-red-500 mt-1">{stats.overdueCount} overdue</p>
                )}
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <ExclamationTriangleIcon className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as InvoiceStatus | '')}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Status</option>
            <option value="DRAFT">Draft</option>
            <option value="PENDING">Pending</option>
            <option value="SENT">Sent</option>
            <option value="PARTIALLY_PAID">Partially Paid</option>
            <option value="PAID">Paid</option>
            <option value="OVERDUE">Overdue</option>
            <option value="CANCELLED">Cancelled</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as InvoiceType | '')}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Types</option>
            <option value="SCHOOL_FEES">School Fees</option>
            <option value="TRANSPORT">Transport</option>
            <option value="LIBRARY">Library</option>
            <option value="UNIFORM">Uniform</option>
            <option value="BOOKS">Books</option>
            <option value="MEALS">Meals</option>
            <option value="ACTIVITIES">Activities</option>
            <option value="EXAM_FEES">Exam Fees</option>
            <option value="OTHER">Other</option>
          </select>

          <label className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={showOverdueOnly}
              onChange={(e) => setShowOverdueOnly(e.target.checked)}
              className="rounded text-primary-600"
            />
            <span className="text-sm text-gray-700">Overdue Only</span>
          </label>

          <Button variant="outline" onClick={() => { fetchInvoices(); fetchStats(); }} className="flex items-center gap-2">
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No invoices found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {invoices.map((invoice) => (
                  <motion.tr
                    key={invoice._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="font-medium text-gray-900">{invoice.invoiceNumber}</p>
                        <p className="text-sm text-gray-500">{invoice.title}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-gray-900">{invoice.customerName}</p>
                        {invoice.studentName && (
                          <p className="text-sm text-gray-500">Student: {invoice.studentName}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                        {getTypeLabel(invoice.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="font-medium text-gray-900">{formatCurrency(invoice.grandTotal, invoice.currency)}</p>
                        {invoice.paidAmount > 0 && invoice.balanceDue > 0 && (
                          <p className="text-sm text-orange-600">Due: {formatCurrency(invoice.balanceDue, invoice.currency)}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[invoice.status].bg} ${statusColors[invoice.status].text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${statusColors[invoice.status].dot}`}></span>
                        {invoice.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(invoice.dueDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => viewInvoice(invoice)}
                          className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="View"
                        >
                          <EyeIcon className="w-5 h-5" />
                        </button>

                        {invoice.status === 'DRAFT' && (
                          <>
                            <button
                              onClick={() => handleSendInvoice(invoice)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Send"
                            >
                              <PaperAirplaneIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteInvoice(invoice)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </>
                        )}

                        {['SENT', 'PARTIALLY_PAID', 'OVERDUE'].includes(invoice.status) && (
                          <button
                            onClick={() => openPaymentModal(invoice)}
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Record Payment"
                          >
                            <BanknotesIcon className="w-5 h-5" />
                          </button>
                        )}

                        {!['PAID', 'CANCELLED', 'REFUNDED'].includes(invoice.status) && invoice.paidAmount === 0 && (
                          <button
                            onClick={() => handleCancelInvoice(invoice)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Cancel"
                          >
                            <XMarkIcon className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {invoices.length} of {totalCount} invoices
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create Invoice Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={resetCreateForm}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Create Invoice</h2>
                  <p className="text-sm text-gray-500 mt-0.5">Create a new invoice for fees and payments</p>
                </div>
                <button onClick={resetCreateForm} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={createForm.handleSubmit(handleCreateInvoice)} className="p-6 space-y-6">
                {/* Step 1: Invoice Type & Student Selection */}
                <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl p-5 border border-primary-100">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 bg-primary-100 rounded-lg">
                      <DocumentTextIcon className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Invoice Type & Recipient</h3>
                      <p className="text-xs text-gray-500">Select the type and who this invoice is for</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Invoice Type *</label>
                      <select
                        {...createForm.register('type')}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                      >
                        <option value="SCHOOL_FEES">School Fees</option>
                        <option value="TRANSPORT">Transport</option>
                        <option value="LIBRARY">Library</option>
                        <option value="UNIFORM">Uniform</option>
                        <option value="BOOKS">Books</option>
                        <option value="MEALS">Meals</option>
                        <option value="ACTIVITIES">Activities</option>
                        <option value="EXAM_FEES">Exam Fees</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Customer Type *</label>
                      <select
                        {...createForm.register('customerType')}
                        onChange={(e) => {
                          const newType = e.target.value as any
                          const currentType = createForm.getValues('customerType')
                          createForm.setValue('customerType', newType)

                          // Don't clear student selection when switching between STUDENT and PARENT
                          // as invoices for students are often billed to parents
                          if (currentType === 'STUDENT' && newType === 'PARENT') {
                            // Keep the student selection - just changing billing entity
                            return
                          }
                          if (currentType === 'PARENT' && newType === 'STUDENT') {
                            // Keep the student selection - just changing billing entity
                            return
                          }

                          // For other changes, clear selections
                          clearAllSelections()
                        }}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                      >
                        <option value="STUDENT">Student</option>
                        <option value="PARENT">Parent</option>
                        <option value="STAFF">Staff</option>
                        <option value="EXTERNAL">External</option>
                      </select>
                    </div>
                  </div>

                  {/* Student Search - Only show when customer type is STUDENT */}
                  {createForm.watch('customerType') === 'STUDENT' && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        <span className="flex items-center gap-1.5">
                          <AcademicCapIcon className="w-4 h-4" />
                          Search Student *
                        </span>
                      </label>
                      <div className="relative">
                        <div className="relative">
                          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            value={studentSearch}
                            onChange={(e) => handleStudentSearchChange(e.target.value)}
                            onFocus={() => setShowStudentDropdown(true)}
                            placeholder="Search by student name, ID, or email..."
                            className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                          {selectedStudent && (
                            <button
                              type="button"
                              onClick={clearStudentSelection}
                              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                            >
                              <XMarkIcon className="w-4 h-4 text-gray-400" />
                            </button>
                          )}
                        </div>

                        {/* Student Search Results Dropdown */}
                        <AnimatePresence>
                          {showStudentDropdown && (studentSearchResults.length > 0 || searchingStudents) && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="absolute z-20 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto"
                            >
                              {searchingStudents ? (
                                <div className="p-4 text-center text-gray-500">
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mx-auto mb-2" />
                                  Searching students...
                                </div>
                              ) : studentSearchResults.length > 0 ? (
                                studentSearchResults.map((student) => (
                                  <button
                                    key={student._id || student.id}
                                    type="button"
                                    onClick={() => handleSelectStudent(student)}
                                    className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                                        <UserIcon className="w-5 h-5 text-primary-600" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-gray-900 truncate">
                                          {student.fullName || `${student.firstName} ${student.lastName}`}
                                        </p>
                                        <div className="flex items-center gap-2 text-xs text-gray-500">
                                          {student.studentId && (
                                            <span className="bg-gray-100 px-1.5 py-0.5 rounded">ID: {student.studentId}</span>
                                          )}
                                          {student.grade?.name && (
                                            <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">
                                              {student.grade.name}
                                            </span>
                                          )}
                                          {student.stream?.name && (
                                            <span className="bg-green-50 text-green-600 px-1.5 py-0.5 rounded">
                                              {student.stream.name}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </button>
                                ))
                              ) : null}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Selected Student Info Card */}
                      {selectedStudent && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-3 p-4 bg-green-50 border border-green-200 rounded-lg"
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <AcademicCapIcon className="w-6 h-6 text-green-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <CheckCircleIcon className="w-5 h-5 text-green-500" />
                                <span className="font-medium text-green-800">Student Selected</span>
                              </div>
                              <p className="font-semibold text-gray-900 mt-1">
                                {selectedStudent.fullName || `${selectedStudent.firstName} ${selectedStudent.lastName}`}
                              </p>
                              <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-600">
                                {selectedStudent.studentId && (
                                  <span>ID: {selectedStudent.studentId}</span>
                                )}
                                {selectedStudent.grade?.name && (
                                  <span className="px-2 py-0.5 bg-white rounded text-xs">
                                    Grade: {selectedStudent.grade.name}
                                  </span>
                                )}
                                {selectedStudent.stream?.name && (
                                  <span className="px-2 py-0.5 bg-white rounded text-xs">
                                    Stream: {selectedStudent.stream.name}
                                  </span>
                                )}
                              </div>
                              {(selectedStudent.primaryParent || selectedStudent.parents?.length) && (
                                <div className="flex items-center gap-1.5 mt-2 text-sm text-gray-600">
                                  <UserGroupIcon className="w-4 h-4" />
                                  <span>Parent: {selectedStudent.primaryParent?.fullName || selectedStudent.parents?.[0]?.fullName}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  )}

                  {/* Parent Search - Only show when customer type is PARENT */}
                  {createForm.watch('customerType') === 'PARENT' && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        <span className="flex items-center gap-1.5">
                          <UserGroupIcon className="w-4 h-4" />
                          Search Parent *
                        </span>
                      </label>
                      <div className="relative">
                        <div className="relative">
                          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            value={parentSearch}
                            onChange={(e) => handleParentSearchChange(e.target.value)}
                            onFocus={() => setShowParentDropdown(true)}
                            placeholder="Search by parent name or email..."
                            className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                          {selectedParent && (
                            <button
                              type="button"
                              onClick={clearParentSelection}
                              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                            >
                              <XMarkIcon className="w-4 h-4 text-gray-400" />
                            </button>
                          )}
                        </div>

                        {/* Parent Search Results Dropdown */}
                        <AnimatePresence>
                          {showParentDropdown && (parentSearchResults.length > 0 || searchingParents) && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="absolute z-20 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto"
                            >
                              {searchingParents ? (
                                <div className="p-4 text-center text-gray-500">
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mx-auto mb-2" />
                                  Searching parents...
                                </div>
                              ) : parentSearchResults.length > 0 ? (
                                parentSearchResults.map((parent) => (
                                  <button
                                    key={parent._id || parent.id}
                                    type="button"
                                    onClick={() => handleSelectParent(parent)}
                                    className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                                        <UserGroupIcon className="w-5 h-5 text-teal-600" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-gray-900 truncate">
                                          {parent.fullName || `${parent.firstName} ${parent.lastName}`}
                                        </p>
                                        <div className="flex items-center gap-2 text-xs text-gray-500">
                                          {parent.email && (
                                            <span className="truncate">{parent.email}</span>
                                          )}
                                          {parent.children && parent.children.length > 0 && (
                                            <span className="bg-teal-50 text-teal-600 px-1.5 py-0.5 rounded">
                                              {parent.children.length} child{parent.children.length > 1 ? 'ren' : ''}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </button>
                                ))
                              ) : null}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Selected Parent Info Card */}
                      {selectedParent && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-3 p-4 bg-teal-50 border border-teal-200 rounded-lg"
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <UserGroupIcon className="w-6 h-6 text-teal-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <CheckCircleIcon className="w-5 h-5 text-teal-500" />
                                <span className="font-medium text-teal-800">Parent Selected</span>
                              </div>
                              <p className="font-semibold text-gray-900 mt-1">
                                {selectedParent.fullName || `${selectedParent.firstName} ${selectedParent.lastName}`}
                              </p>
                              {selectedParent.email && (
                                <p className="text-sm text-gray-600 mt-1">{selectedParent.email}</p>
                              )}

                              {/* Child Selection - Show when parent has children */}
                              {selectedParent.children && selectedParent.children.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-teal-200">
                                  <label className="block text-sm font-medium text-teal-800 mb-2">
                                    <span className="flex items-center gap-1.5">
                                      <AcademicCapIcon className="w-4 h-4" />
                                      Select Student (Invoice For) *
                                    </span>
                                  </label>
                                  {selectedParent.children.length === 1 ? (
                                    // Single child - auto-selected
                                    <div className="flex items-center gap-2 p-2 bg-white rounded-lg border border-teal-300">
                                      <UserIcon className="w-5 h-5 text-teal-600" />
                                      <span className="font-medium text-gray-900">{selectedParent.children[0].fullName}</span>
                                      <CheckCircleIcon className="w-4 h-4 text-green-500 ml-auto" />
                                    </div>
                                  ) : (
                                    // Multiple children - show selection
                                    <div className="space-y-2">
                                      {selectedParent.children.map((child) => (
                                        <button
                                          key={child.id}
                                          type="button"
                                          onClick={() => handleSelectChildFromParent(child)}
                                          className={`w-full flex items-center gap-2 p-2 rounded-lg border transition-colors ${
                                            selectedChildFromParent?.id === child.id
                                              ? 'bg-teal-100 border-teal-400'
                                              : 'bg-white border-gray-200 hover:border-teal-300 hover:bg-teal-50'
                                          }`}
                                        >
                                          <UserIcon className="w-5 h-5 text-teal-600" />
                                          <span className="font-medium text-gray-900">{child.fullName}</span>
                                          {child.studentId && (
                                            <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                              ID: {child.studentId}
                                            </span>
                                          )}
                                          {selectedChildFromParent?.id === child.id && (
                                            <CheckCircleIcon className="w-4 h-4 text-green-500 ml-auto" />
                                          )}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Warning if parent has no children */}
                              {(!selectedParent.children || selectedParent.children.length === 0) && (
                                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                                  <span>This parent has no linked students.</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  )}

                  {/* Staff Search - Only show when customer type is STAFF */}
                  {createForm.watch('customerType') === 'STAFF' && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        <span className="flex items-center gap-1.5">
                          <UserIcon className="w-4 h-4" />
                          Search Staff/Teacher *
                        </span>
                      </label>
                      <div className="relative">
                        <div className="relative">
                          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            value={staffSearch}
                            onChange={(e) => handleStaffSearchChange(e.target.value)}
                            onFocus={() => setShowStaffDropdown(true)}
                            placeholder="Search by staff name or email..."
                            className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                          {selectedStaff && (
                            <button
                              type="button"
                              onClick={clearStaffSelection}
                              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                            >
                              <XMarkIcon className="w-4 h-4 text-gray-400" />
                            </button>
                          )}
                        </div>

                        {/* Staff Search Results Dropdown */}
                        <AnimatePresence>
                          {showStaffDropdown && (staffSearchResults.length > 0 || searchingStaff) && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="absolute z-20 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto"
                            >
                              {searchingStaff ? (
                                <div className="p-4 text-center text-gray-500">
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mx-auto mb-2" />
                                  Searching staff...
                                </div>
                              ) : staffSearchResults.length > 0 ? (
                                staffSearchResults.map((staff) => (
                                  <button
                                    key={staff._id || staff.id}
                                    type="button"
                                    onClick={() => handleSelectStaff(staff)}
                                    className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                                        <UserIcon className="w-5 h-5 text-indigo-600" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-gray-900 truncate">
                                          {staff.fullName || `${staff.firstName} ${staff.lastName}`}
                                        </p>
                                        <div className="flex items-center gap-2 text-xs text-gray-500">
                                          {staff.email && (
                                            <span className="truncate">{staff.email}</span>
                                          )}
                                          <span className="bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded">
                                            Staff
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </button>
                                ))
                              ) : null}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Selected Staff Info Card */}
                      {selectedStaff && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-3 p-4 bg-indigo-50 border border-indigo-200 rounded-lg"
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <UserIcon className="w-6 h-6 text-indigo-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <CheckCircleIcon className="w-5 h-5 text-indigo-500" />
                                <span className="font-medium text-indigo-800">Staff Selected</span>
                              </div>
                              <p className="font-semibold text-gray-900 mt-1">
                                {selectedStaff.fullName || `${selectedStaff.firstName} ${selectedStaff.lastName}`}
                              </p>
                              {selectedStaff.email && (
                                <p className="text-sm text-gray-600 mt-1">{selectedStaff.email}</p>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  )}
                </div>

                {/* Step 2: Customer Details (auto-filled or manual entry) */}
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 bg-gray-200 rounded-lg">
                      <UserIcon className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Bill To (Customer Details)</h3>
                      <p className="text-xs text-gray-500">
                        {(createForm.watch('customerType') === 'STUDENT' && selectedStudent) ||
                         (createForm.watch('customerType') === 'PARENT' && selectedParent) ||
                         (createForm.watch('customerType') === 'STAFF' && selectedStaff)
                          ? 'Auto-filled from selected record - you can edit if needed'
                          : 'Enter the customer information'
                        }
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Customer Name *</label>
                      <Input {...createForm.register('customerName')} placeholder="Full name of the person to bill" />
                      {createForm.formState.errors.customerName && (
                        <p className="text-sm text-red-500 mt-1">{createForm.formState.errors.customerName.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Student Name</label>
                      <Input
                        {...createForm.register('studentName')}
                        placeholder="Student's name (if different)"
                        disabled={createForm.watch('customerType') === 'STUDENT' && !!selectedStudent}
                        className={createForm.watch('customerType') === 'STUDENT' && selectedStudent ? 'bg-gray-100' : ''}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                      <Input {...createForm.register('customerEmail')} type="email" placeholder="customer@email.com" />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
                      <Input {...createForm.register('customerPhone')} placeholder="+254..." />
                    </div>
                  </div>
                </div>

                {/* Step 3: Invoice Details */}
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 bg-gray-200 rounded-lg">
                      <CalendarIcon className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Invoice Details</h3>
                      <p className="text-xs text-gray-500">Set the invoice title, due date, and term information</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Invoice Title *</label>
                      <Input {...createForm.register('title')} placeholder="e.g., Term 1 School Fees 2025" />
                      {createForm.formState.errors.title && (
                        <p className="text-sm text-red-500 mt-1">{createForm.formState.errors.title.message}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Due Date *</label>
                        <Input {...createForm.register('dueDate')} type="date" />
                        {createForm.formState.errors.dueDate && (
                          <p className="text-sm text-red-500 mt-1">{createForm.formState.errors.dueDate.message}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Term</label>
                        <Input {...createForm.register('termName')} placeholder="e.g., Term 1" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Academic Year</label>
                        <Input {...createForm.register('academicYear')} placeholder="e.g., 2025" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                      <textarea
                        {...createForm.register('description')}
                        rows={2}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                        placeholder="Optional description for the invoice..."
                      />
                    </div>
                  </div>
                </div>

                {/* Step 4: Invoice Items */}
                <div className="bg-white rounded-xl p-5 border-2 border-dashed border-gray-300">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <CurrencyDollarIcon className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Invoice Items</h3>
                        <p className="text-xs text-gray-500">Add the items/services being billed</p>
                      </div>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={addItem}>
                      <PlusIcon className="w-4 h-4 mr-1" />
                      Add Item
                    </Button>
                  </div>

                  {/* Items Header */}
                  <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 bg-gray-100 rounded-lg mb-2 text-xs font-medium text-gray-500 uppercase">
                    <div className="col-span-5">Description</div>
                    <div className="col-span-2 text-center">Quantity</div>
                    <div className="col-span-2 text-center">Unit Price</div>
                    <div className="col-span-2 text-right">Total</div>
                    <div className="col-span-1"></div>
                  </div>

                  <div className="space-y-2">
                    {invoiceItems.map((item, index) => (
                      <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-3 p-3 bg-gray-50 rounded-lg items-center">
                        <div className="md:col-span-5">
                          <label className="md:hidden text-xs font-medium text-gray-500 mb-1 block">Description</label>
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                            placeholder="Item description"
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="md:hidden text-xs font-medium text-gray-500 mb-1 block">Quantity</label>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                            placeholder="1"
                            min="1"
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-center focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="md:hidden text-xs font-medium text-gray-500 mb-1 block">Unit Price (Ksh)</label>
                          <input
                            type="number"
                            value={item.unitPrice}
                            onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-center focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                        <div className="md:col-span-2 text-right">
                          <label className="md:hidden text-xs font-medium text-gray-500 mb-1 block">Total</label>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(item.quantity * item.unitPrice)}
                          </span>
                        </div>
                        <div className="md:col-span-1 flex justify-end">
                          {invoiceItems.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove item"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Total */}
                  <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end">
                    <div className="bg-primary-50 border border-primary-200 px-6 py-3 rounded-lg">
                      <span className="text-sm text-primary-700">Grand Total: </span>
                      <span className="text-2xl font-bold text-primary-900">{formatCurrency(calculateTotal())}</span>
                    </div>
                  </div>
                </div>

                {/* Step 5: Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes (visible to customer)</label>
                  <textarea
                    {...createForm.register('notes')}
                    rows={2}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Payment instructions, bank details, terms, etc..."
                  />
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500">Invoice will be saved as a draft</p>
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={resetCreateForm}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={createForm.formState.isSubmitting}>
                      {createForm.formState.isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <DocumentTextIcon className="w-4 h-4 mr-2" />
                          Create Invoice
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && selectedInvoice && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowPaymentModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-gray-200 px-6 py-4">
                <h2 className="text-xl font-semibold text-gray-900">Record Payment</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Invoice: {selectedInvoice.invoiceNumber} | Balance Due: {formatCurrency(selectedInvoice.balanceDue, selectedInvoice.currency)}
                </p>
              </div>

              <form onSubmit={paymentForm.handleSubmit(handleRecordPayment)} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                  <Input
                    type="number"
                    step="0.01"
                    {...paymentForm.register('amount', { valueAsNumber: true })}
                  />
                  {paymentForm.formState.errors.amount && (
                    <p className="text-sm text-red-500 mt-1">{paymentForm.formState.errors.amount.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
                  <select
                    {...paymentForm.register('paymentMethod')}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="CASH">Cash</option>
                    <option value="MPESA">M-Pesa</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="CARD">Card</option>
                    <option value="CHEQUE">Cheque</option>
                    <option value="WALLET">Wallet</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
                  <Input {...paymentForm.register('reference')} placeholder="Transaction ID, receipt number, etc." />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    {...paymentForm.register('notes')}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="Optional notes..."
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowPaymentModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={paymentForm.formState.isSubmitting}>
                    {paymentForm.formState.isSubmitting ? 'Recording...' : 'Record Payment'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default InvoicesPage
